import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://achtungroyale.io";
// The game/site keep their own name; the ORGANIZATION behind them is the studio.
const SITE_NAME = "Achtung Royale";
const ORG_NAME = "Skill Check Games";
const ORG_URL = "https://skillcheckgames.com";

function JsonLd({ data }: { data: Record<string, unknown> }) {
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}

export function WebSiteJsonLd() {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_URL,
      }}
    />
  );
}

export function OrganizationJsonLd() {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "Organization",
        name: ORG_NAME,
        url: ORG_URL,
        logo: `${SITE_URL}/pwa-512x512.png`,
      }}
    />
  );
}

// Tells search engines this is a free, browser-playable game (richer results).
export function VideoGameJsonLd() {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "VideoGame",
        name: SITE_NAME,
        url: SITE_URL,
        image: `${SITE_URL}/pwa-512x512.png`,
        description:
          "A free, modern browser version of the classic Achtung, die Kurve! Play vs AI or locally with friends. No download.",
        applicationCategory: "Game",
        operatingSystem: "Web browser",
        gamePlatform: "Web browser",
        genre: ["Arcade", "Multiplayer", "Casual"],
        playMode: ["SinglePlayer", "CoOp", "MultiPlayer"],
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        publisher: { "@type": "Organization", name: ORG_NAME, url: ORG_URL },
      }}
    />
  );
}

// A breadcrumb trail (Home › … › current page). Helps search engines show the
// site hierarchy under a result. Pass the trail in order; the last item is the
// current page. Positions are 1-based per schema.org.
export function BreadcrumbJsonLd({ items }: { items: { name: string; url: string }[] }) {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((it, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: it.name,
          item: it.url,
        })),
      }}
    />
  );
}

export function NewsArticleJsonLd({
  title,
  description,
  datePublished,
  url,
}: {
  title: string;
  description: string;
  datePublished: string;
  url: string;
}) {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        headline: title,
        description,
        datePublished,
        url,
        publisher: {
          "@type": "Organization",
          name: ORG_NAME,
          url: ORG_URL,
        },
      }}
    />
  );
}
